import type {DiffDelta} from '@github-ui/diff-file-tree'
import {DiffFileTree} from '@github-ui/diff-file-tree'
import {FileFilterShared} from '@github-ui/diff-file-tree/file-filter'
import {debounce} from '@github/mini-throttle'
import {useState, useRef, useMemo} from 'react'
import {getFileExtension} from '../../utils/get-file-extension'

export type FileTreeProps = {
  activeFilePath?: string
  diffs: DiffDelta[]
  onFileSelected: (file: DiffDelta) => void
}

export function FileTree({diffs, onFileSelected}: FileTreeProps) {
  const extensions = diffs.map(diff => getFileExtension(diff.path))
  const fileExtensions = useMemo(() => (extensions ? new Set(extensions) : undefined), [extensions])

  const [filterText, setFilterText] = useState('')

  const filteredDiffs = filterText
    ? diffs.filter(diff => diff.path.toLowerCase().includes(filterText.toLowerCase()))
    : diffs

  return (
    <div className="d-flex flex-column gap-2">
      <FileFilter filterText={filterText} onFilterTextChange={setFilterText} fileExtensions={fileExtensions} />
      <DiffFileTree activeFilePath="" diffs={filteredDiffs} onSelect={onFileSelected} />
    </div>
  )
}

type FileFilterProps = {
  filterText: string
  onFilterTextChange(filterText: string): void
  fileExtensions?: Set<string>
}

function FileFilter({filterText, onFilterTextChange, fileExtensions}: FileFilterProps) {
  const debouncedOnSearch = useRef(debounce((newQuery: string) => onFilterTextChange(newQuery), 250))

  return (
    <FileFilterShared
      fileExtensions={fileExtensions}
      filterText={filterText}
      onFilterTextChange={text => debouncedOnSearch.current(text)}
    />
  )
}

try{ FileTree.displayName ||= 'FileTree' } catch {}
try{ FileFilter.displayName ||= 'FileFilter' } catch {}